import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { taskcontants } from '@/constants/taskConstants';
import { GetTrailerListAsync } from '@/service/ManhattanService';
import {
  CompleteActionException,
  CompleteActionExceptionItem,
  CreateAuditTaskUpdateTaskDetails,
  QuestionFlow,
  QuestionModel,
  UpdateTaskResponseUpdateTaskDetail,
} from '@/service/types/audittasktypes';
import { store } from '@/store';
import { cn } from '@/utils/cn';

import { YmsAlertDialog } from './dashboard/YmsAlertDialog';
import { LoadingOverlay } from './ui/LoadingOverlay';

import type { TrailerModel } from '@/service/types/movetasktypes';

interface AuditFixDialogProps {
  screen: string;
  open: boolean;
  task: CreateAuditTaskUpdateTaskDetails | null;
  onClose: () => void;
  onComplete: (task: UpdateTaskResponseUpdateTaskDetail) => void;
  CompleteActionExceptions: CompleteActionException | null;
}

const OTHER_TRAILER_BASE: TrailerModel = {
  Trailer_Name: '',
  Carrier_Name: '',
  TrailerStatusName: '',
  TrailerConditionName: '',
  TrailerId: -1,
  Carrier_scac: '',
  CurrentLocationType: '',
  CurrentLocationZone: '',
  CurrentLocationName: '',
  Location: '',
  IsEditing: false,
  IsTrailerDamaged: false,
  TrailerComments: '',
  CheckInTime: '',
  DwellingTime: '',
  TrailerStatus: '',
  PalletsLoaded: '',
  Shipment: '',
  Order: '',
  ScheduledPickup: '',
  CustomerName: '',
  Item: '',
  PurchaseOrder: '',
  DriverName: '',
  DriverCell: '',
  DwellingTimeHours: '',
  TrailerCondition: '',
  LockReasonCode: '',
  LockReasonCodeName: '',
};

export function AuditFixDialog({
  screen,
  open,
  task,
  onClose,
  onComplete,
  CompleteActionExceptions,
}: AuditFixDialogProps) {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrailer, setSelectedTrailer] = useState<TrailerModel | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(true);
  const [currentTask, setCurrentTask] = useState<UpdateTaskResponseUpdateTaskDetail>();
  const [trailersList, setTrailersList] = useState<TrailerModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [otherTrailerNumber, setOtherTrailerNumber] = useState('');
  const [otherCarrierName, setOtherCarrierName] = useState('');
  const [taskComments, setTaskComments] = useState('');
  const [trailerComments, setTrailerComments] = useState('');
  const [displayedQuestions, setDisplayedQuestions] = useState<QuestionModel[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const hasExistingTrailer = task ? task.TrailerName.trim() !== '' : false;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    void initdata();
    if (open) {
      setSearchQuery('');
      setSelectedTrailer(null);
      setDropdownOpen(true);
      setOtherTrailerNumber('');
      setOtherCarrierName('');
      setTaskComments('');
      setTrailerComments('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const initdata = async () => {
    setIsLoading(true);
    if (!task) return;
    const auditTask: CreateAuditTaskUpdateTaskDetails = task;

    const tempCurrentTask: UpdateTaskResponseUpdateTaskDetail = {
      Task_details: {
        Task_type: taskcontants.AuditTaskType,
        Audit_location_name: auditTask.AuditLocationName,
        Audit_location_type: auditTask.AuditLocationType,
        Audit_location_zone: auditTask.AuditLocationZone,
        Carrier_name: auditTask.CarrierCode,
        Carrier_company_name: auditTask.CarrierName,
        Trailer_name: auditTask.TrailerName,
        Action_code: '',
        Org_id: '',
        Task_id: '',
        Status: '',
        Assigned_user_id: '',
        Created_source: '',
        Created_datetime: '',
        Last_updated_datetime: '',
        Last_updated_source: '',
        Priority: '',
        Yard_id: '',
        Task_start_datetime: '',
        Task_end_datetime: '',
        Task_release_datetime: '',
        Task_comments: '',
        Trailer_id: '',
        Trailer_comments: '',
        Move_src_type: '',
        Move_src_zone: '',
        Move_src_name: '',
        Move_dst_type: '',
        Move_dst_zone: '',
        Move_dst_name: '',
        Exception_code: '',
        Elapsed_time: '',
        Task_assigned_time: '',
        Alt_Dst_Zone: '',
        Alt_Dst_Name: '',
        Alt_Trailer_Valid: '',
        Alt_Carrier: '',
        Alt_Trailer: '',
      },
      Task_type: taskcontants.AuditTaskType,
      Trailer_name: auditTask.TrailerName,
      Carrier_name: auditTask.CarrierCode,
      Status: '',
      Status_message: '',
      Status_code: '',
      Org_id: '',
      Task_id: '',
      User_id: '',
      Action_type: '',
      Action_datetime: '',
      Exception_code: '',
      Task_comments: '',
      Trailer_comments: '',
      TaskStartedDateTime: '',
      SelectedIndex: 0,
      CurrentLoggedInUser: '',
      AlternateTrailerValid: '',
      AlternateCarrier: '',
      AlternateTrailerName: '',
      AlternateDestinationZone: '',
      AlternateDestinationName: '',
      OverrideReasonCode: '',
      TaskCommentOverrideReasonCode: '',
    };

    setCurrentTask(tempCurrentTask);

    const globalSettings = store.getState().global.settings;
    const completeTaskFlowJson =
      i18n.language === 'es' && globalSettings?.COMPLETE_TASK_FLOW_SPANISH
        ? globalSettings.COMPLETE_TASK_FLOW_SPANISH
        : (globalSettings?.COMPLETE_TASK_FLOW ?? undefined);
    if (!completeTaskFlowJson) return;

    let questionFlow: QuestionFlow | null = null;

    questionFlow = JSON.parse(completeTaskFlowJson) as QuestionFlow;

    let firstQuestion: QuestionModel;
    if (tempCurrentTask.Task_type === taskcontants.MoveTaskType) {
      firstQuestion = questionFlow.move;
    } else if (screen === 'CreateAudit' && tempCurrentTask.Trailer_name.trim() === '') {
      firstQuestion = questionFlow.fix_without_trailer;
    } else if (screen === 'CreateAudit' && tempCurrentTask.Trailer_name.trim() !== '') {
      firstQuestion = questionFlow.fix;
    } else if (tempCurrentTask.Trailer_name.trim() === '') {
      firstQuestion = questionFlow.audit_without_trailer;
    } else {
      firstQuestion = questionFlow.audit;
    }

    if (firstQuestion.QuestionType === taskcontants.TrailerQuestion) {
      setIsLoading(true);
      try {
        const trailerListRes: TrailerModel[] = await GetTrailerListAsync();
        setTrailersList(trailerListRes);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error at catch block', err);
      } finally {
        setIsLoading(false);
      }
    }
    setDisplayedQuestions([{ ...firstQuestion }]);
  };

  const otherTrailerItem = useMemo<TrailerModel>(
    () => ({ ...OTHER_TRAILER_BASE, Trailer_Name: t('completeTask.otherTrailer') }),
    [t]
  );

  const filteredTrailers = useMemo(() => {
    if (!searchQuery.trim()) return [otherTrailerItem, ...trailersList];
    const q = searchQuery.toLowerCase().trim();
    const filtered = trailersList.filter(
      (tr) => tr.Trailer_Name.toLowerCase().includes(q) || tr.Carrier_Name.toLowerCase().includes(q)
    );
    return [otherTrailerItem, ...filtered];
  }, [trailersList, searchQuery, otherTrailerItem]);

  const isOtherTrailer = selectedTrailer?.TrailerId === -1;

  const isFlowComplete =
    displayedQuestions.length > 0 &&
    displayedQuestions[displayedQuestions.length - 1]?.QuestionType === 'COMPLETE';

  const hasTrailerQuestion = displayedQuestions.some(
    (q) => q.QuestionType === taskcontants.TrailerQuestion
  );

  const isCompleteEnabled = (() => {
    if (!isFlowComplete) return false;

    if (hasTrailerQuestion) {
      if (!selectedTrailer) return false;
      if (isOtherTrailer) {
        return (
          otherTrailerNumber.trim() !== '' &&
          /\d/.test(otherTrailerNumber) &&
          otherCarrierName.trim() !== ''
        );
      }
    }
    return true;
  })();

  function hanleOkClick() {
    setAlertOpen(false);
  }

  function handleComplete(): void {
    if (!currentTask) {
      onClose();
      return;
    }

    if (selectedTrailer?.Trailer_Name != null) {
      if (selectedTrailer.TrailerId !== -1) {
        if (
          currentTask.Task_details.Trailer_name === selectedTrailer.Trailer_Name &&
          currentTask.Task_details.Carrier_company_name === selectedTrailer.Carrier_Name
        ) {
          setAlertOpen(true);
          <YmsAlertDialog
            open={alertOpen}
            title={t('auditAlert.selectedTrailerTitle')}
            message={t('auditAlert.selectedTrailerMessage')}
            okText={t('common.ok')}
            onOk={hanleOkClick}
          />;
          return;
        }
        currentTask.Task_details.Alt_Trailer = selectedTrailer.Trailer_Name;
        currentTask.Task_details.Alt_Carrier = selectedTrailer.Carrier_scac;
        currentTask.Task_details.Alt_Trailer_Valid = 'true';
      } else {
        if (
          otherTrailerNumber.toUpperCase() == currentTask.Task_details.Trailer_name.toUpperCase() &&
          otherTrailerNumber.toUpperCase() ==
            currentTask.Task_details.Carrier_company_name.toUpperCase()
        ) {
          setAlertOpen(true);
          <YmsAlertDialog
            open={alertOpen}
            title={t('auditAlert.selectedTrailerTitle')}
            message={t('auditAlert.selectedTrailerMessage')}
            okText={t('common.ok')}
            onOk={hanleOkClick}
          />;
          return;
        } else if (
          trailersList.some(
            (x) =>
              x.Trailer_Name.toUpperCase() == otherTrailerNumber.toUpperCase() &&
              x.Carrier_Name.toUpperCase() == otherTrailerNumber.toUpperCase()
          )
        ) {
          setAlertOpen(true);
          <YmsAlertDialog
            open={alertOpen}
            title={t('auditAlert.otherTrailerTitle')}
            message={t('auditAlert.otherTrailerMessage')}
            okText={t('common.ok')}
            onOk={hanleOkClick}
          />;
          return;
        }

        currentTask.Task_details.Alt_Trailer = otherTrailerNumber;
        currentTask.Task_details.Alt_Carrier = otherCarrierName;
        currentTask.Task_details.Alt_Trailer_Valid = 'false';
      }
    } else {
      currentTask.Task_details.Alt_Trailer_Valid = 'false';
      currentTask.Task_details.Alt_Trailer = '';
      currentTask.Task_details.Alt_Carrier = '';
    }

    if (selectedTrailer != null && selectedTrailer.CurrentLocationName !== '') {
      currentTask.Task_details.Alt_Dst_Name = selectedTrailer.CurrentLocationName;
      currentTask.Task_details.Alt_Dst_Zone = selectedTrailer.CurrentLocationZone;
    } else {
      currentTask.Task_details.Alt_Dst_Name = '';
      currentTask.Task_details.Alt_Dst_Zone = '';
    }

    currentTask.Trailer_name = trailerNumber;
    currentTask.Task_details.Trailer_name = trailerNumber;
    const exceptionQuestion = displayedQuestions.find((q) => q.QuestionType === 'EXCEPTION');
    currentTask.Exception_code = exceptionQuestion?.Question ?? '';

    currentTask.TaskCommentOverrideReasonCode = '';
    currentTask.OverrideReasonCode = '';
    onComplete(currentTask);
  }

  // Build question path with auto-advancing EXCEPTION questions
  function buildQuestionPath(
    questions: QuestionModel[],
    nextQuestion: QuestionModel | null
  ): QuestionModel[] {
    if (!nextQuestion) return questions;

    const newQuestion: QuestionModel = { ...nextQuestion, QuestionSelection: 'NotSelected' };
    const withNext = [...questions, newQuestion];

    // Auto-advance EXCEPTION questions (they don't need user input)
    if (nextQuestion.QuestionType === 'EXCEPTION' && nextQuestion.YesOption) {
      return [...withNext, { ...nextQuestion.YesOption, QuestionSelection: 'NotSelected' }];
    }

    return withNext;
  }

  async function handleQuestionAnswer(questionIndex: number, answer: 'Yes' | 'No') {
    setSelectedTrailer(null);
    setSearchQuery('');
    setDropdownOpen(true);
    setOtherTrailerNumber('');
    setOtherCarrierName('');

    setDisplayedQuestions((prev) => {
      const updated = prev.slice(0, questionIndex + 1);
      const currentQ = updated[questionIndex];
      if (!currentQ) return prev;

      updated[questionIndex] = {
        ...currentQ,
        QuestionSelection: answer === 'Yes' ? 'Yes' : 'No',
      };

      const nextQuestion = answer === 'Yes' ? currentQ.YesOption : currentQ.NoOption;
      return buildQuestionPath(updated, nextQuestion);
    });

    if (answer === 'Yes') {
      setIsLoading(true);
      try {
        const trailerListRes: TrailerModel[] = await GetTrailerListAsync();
        setTrailersList(trailerListRes);
      } finally {
        setIsLoading(false);
      }
    }
  }

  function handleTrailerAdvance(trailer: TrailerModel) {
    setSelectedTrailer(trailer);
    setSearchQuery('');
    setDropdownOpen(false);

    setDisplayedQuestions((prev) => {
      const trailerIndex = prev.findIndex((q) => q.QuestionType === taskcontants.TrailerQuestion);
      if (trailerIndex === -1) return prev;

      const updated = prev.slice(0, trailerIndex + 1);
      const trailerQ = updated[trailerIndex];
      if (!trailerQ) return prev;

      const isOther = trailer.TrailerId === -1;
      const nextQuestion = isOther ? trailerQ.NoOption : trailerQ.YesOption;
      return buildQuestionPath(updated, nextQuestion);
    });
  }

  if (!open || !task) return null;

  const destinationLocation = `${task.AuditLocationZone} - ${task.AuditLocationName} (${task.AuditLocationType})`;
  const trailerNumber = task.TrailerName.trim() || '-';
  const carrierName = task.CarrierName.trim() || '-';

  // Get the relevant exception list based on task type
  const exceptionItems: CompleteActionExceptionItem[] = CompleteActionExceptions
    ? hasExistingTrailer
      ? CompleteActionExceptions.audit
      : CompleteActionExceptions.audit_without_trailer
    : [];

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-screen-md max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-xl animate-in zoom-in-95 duration-150 flex flex-col">
        {/* Scrollable content area — entire content scrolls */}
        <div className="mt-6 mr-6 flex-1 overflow-y-auto min-h-0 scrollbar-thin-dark">
          {/* Header: Trailer Number & Destination Location */}
          <div className="pl-6 pb-4">
            <div className="flex justify-between">
              <div>
                <p className="text-sm font-poppins font-bold text-[#0B1B25]">
                  {t('auditFix.trailerNumber')}
                </p>
                <p className="text-base font-poppins text-[#243742] mt-1">{trailerNumber}</p>
              </div>
              <div>
                <p className="text-sm font-poppins font-bold text-[#0B1B25]">
                  {t('auditFix.destinationLocation')}
                </p>
                <p className="text-base font-poppins text-[#243742] mt-1">{destinationLocation}</p>
              </div>
            </div>

            {/* Carrier Name */}
            <div className="mt-4">
              <p className="text-sm font-poppins font-bold text-[#0B1B25]">
                {t('auditFix.carrierName')}
              </p>
              <p className="text-base font-poppins text-[#243742] mt-1">{carrierName}</p>
            </div>
          </div>

          {/* Divider */}
          <div className="ml-6 mr-2 border-t border-gray-300" />
          <div className="pl-6 pt-4">
            {/* Dynamic Question Flow */}
            {displayedQuestions.length > 0 ? (
              displayedQuestions.map((question, index) => {
                // YES/NO question type — render question text + Yes/No buttons
                if (question.QuestionType === 'YES/NO') {
                  return (
                    <div key={`yesno-${String(index)}`} className="mb-4">
                      <p className="text-sm font-poppins text-[#445E71] mb-3">
                        {question.Question}
                      </p>
                      <div className="flex gap-7">
                        <button
                          type="button"
                          onClick={() => {
                            void handleQuestionAnswer(index, 'Yes');
                          }}
                          className={cn(
                            'h-10 w-[100px] rounded border text-sm font-poppins font-medium transition-colors',
                            question.QuestionSelection === 'Yes'
                              ? 'bg-[#003366] text-white border-[#003366] border-[1.5px] hover:bg-[#002952]'
                              : 'bg-white text-[#003366] border-[#003366] border-[1.5px] hover:bg-[#e6e6e6]'
                          )}
                        >
                          {t('common.yes')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            void handleQuestionAnswer(index, 'No');
                          }}
                          className={cn(
                            'h-10 w-[100px] rounded border text-sm font-poppins font-medium transition-colors',
                            question.QuestionSelection === 'No'
                              ? 'bg-[#003366] text-white border-[#003366] border-[1.5px] hover:bg-[#002952]'
                              : 'bg-white text-[#003366] border-[#003366] border-[1.5px] hover:bg-[#e6e6e6]'
                          )}
                        >
                          {t('common.no')}
                        </button>
                      </div>
                    </div>
                  );
                }

                // TRAILER question type — render trailer dropdown + Other Trailer fields
                if (question.QuestionType === taskcontants.TrailerQuestion) {
                  if (isLoading) {
                    return <LoadingOverlay key={`trailer-${String(index)}`} open={true} />;
                  }

                  return (
                    <div key={`trailer-${String(index)}`}>
                      <p className="text-[13px] font-poppins text-[#243742] mb-2">
                        {question.Question}
                      </p>

                      {/* Search / Dropdown input */}
                      <div className="w-80 rounded-md border-[1.4px] border-[#d1d1d1] bg-white">
                        <input
                          type="text"
                          placeholder={t('auditFix.searchPlaceholder')}
                          value={
                            selectedTrailer
                              ? selectedTrailer.Carrier_Name
                                ? `${selectedTrailer.Trailer_Name} | ${selectedTrailer.Carrier_Name}`
                                : selectedTrailer.Trailer_Name
                              : searchQuery
                          }
                          onFocus={() => {
                            setDropdownOpen(true);
                          }}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setSelectedTrailer(null);
                            setDropdownOpen(true);
                          }}
                          className="h-10 w-full bg-transparent px-3 text-[13px] font-poppins text-[#243742] placeholder:text-[#666666] outline-none"
                        />
                      </div>

                      {/* Trailer list dropdown */}
                      {dropdownOpen && (
                        <div className="w-80 max-h-[200px] overflow-y-auto border-[1.4px] border-[#d1d1d1] rounded scrollbar-thin-dark">
                          {filteredTrailers.length === 0 ? (
                            <p className="text-center text-[13px] font-poppins text-[#243742] py-4">
                              {t('auditFix.noTrailersFound')}
                            </p>
                          ) : (
                            filteredTrailers.map((trailer) => {
                              const isSelected =
                                selectedTrailer?.Trailer_Name === trailer.Trailer_Name;
                              const displayText = trailer.Carrier_Name
                                ? `${trailer.Trailer_Name}  |  ${trailer.Carrier_Name}`
                                : trailer.Trailer_Name;
                              return (
                                <button
                                  key={trailer.Trailer_Name}
                                  type="button"
                                  onClick={() => {
                                    handleTrailerAdvance(trailer);
                                  }}
                                  className={cn(
                                    'w-full text-left px-3 py-3 text-sm font-poppins text-[#0B1B25] transition-colors',
                                    isSelected ? 'bg-[#d1d1d1]' : 'hover:bg-[#e8e8e8]'
                                  )}
                                >
                                  {displayText}
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}

                      {/* "Other Trailer" selected — show manual entry fields */}
                      {isOtherTrailer && (
                        <div className="mt-4">
                          <div className="flex gap-8">
                            <div>
                              <p className="text-[13px] font-poppins text-[#243742] mb-1">
                                {t('auditFix.enterTrailerNumber')}
                              </p>
                              <input
                                type="text"
                                value={otherTrailerNumber}
                                maxLength={20}
                                onChange={(e) => {
                                  setOtherTrailerNumber(
                                    e.target.value.replace(/[^a-zA-Z0-9\-_ ]/g, '')
                                  );
                                }}
                                className="h-10 w-[200px] rounded-md border-[1.4px] border-[#d1d1d1] px-3 text-sm font-poppins text-[#243742] outline-none"
                              />
                            </div>
                            <div>
                              <p className="text-[13px] font-poppins text-[#243742] mb-1">
                                {t('auditFix.enterCarrierName')}
                              </p>
                              <input
                                type="text"
                                value={otherCarrierName}
                                maxLength={50}
                                onChange={(e) => {
                                  setOtherCarrierName(
                                    e.target.value.replace(/[^a-zA-Z0-9\-_ ]/g, '')
                                  );
                                }}
                                className="h-10 w-[200px] rounded-md border-[1.4px] border-[#d1d1d1] px-3 text-sm font-poppins text-[#243742] outline-none"
                              />
                            </div>
                          </div>
                          <p className="text-[15px] font-poppins font-bold text-[#1358a1] mt-2">
                            {t('auditFix.trailerValidationNote')}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                }

                // EXCEPTION question type — show warning for non-OT codes
                if (question.QuestionType === 'EXCEPTION') {
                  const exceptionMatch = exceptionItems.find(
                    (item) => item.code === question.Question
                  );
                  const exceptionText = isOtherTrailer
                    ? t('auditFix.invalidTrailerInLocation')
                    : (exceptionMatch?.text ?? question.Question);
                  return (
                    <div
                      key={`exception-${String(index)}`}
                      className="flex items-center gap-2 mt-2"
                    >
                      <img
                        src="/images/exception.svg"
                        alt=""
                        className="h-7 w-7"
                        aria-hidden="true"
                      />
                      <span className="text-lg font-poppins font-medium text-[#ffa319]">
                        {exceptionText}
                      </span>
                    </div>
                  );
                }

                return null;
              })
            ) : (
              <LoadingOverlay open={true} />
            )}
          </div>

          {/* Task Comments — always shown */}
          <div className="pl-6 pr-2 pt-4">
            <p className="text-[13px] font-poppins text-[#0d262e] mb-1">
              {t('auditFix.taskComments')}
            </p>
            <textarea
              value={taskComments}
              onChange={(e) => {
                setTaskComments(e.target.value);
              }}
              maxLength={250}
              placeholder={t('auditFix.taskCommentsPlaceholder')}
              className="w-full h-20 rounded-lg border-[1.4px] border-[#d1d1d1] px-3 py-2 text-[13px] font-poppins text-[#0d262e] placeholder:text-[#425b6e] outline-none resize-none"
            />
          </div>

          {/* Trailer Comments — only shown when location has an existing trailer */}
          {hasExistingTrailer && (
            <div className="pl-6 pt-3 pr-2 pb-2">
              <p className="text-[13px] font-poppins text-[#0d262e] mb-1">
                {t('auditFix.trailerComments')}
              </p>
              <textarea
                value={trailerComments}
                onChange={(e) => {
                  setTrailerComments(e.target.value);
                }}
                maxLength={250}
                placeholder={t('auditFix.trailerCommentsPlaceholder')}
                className="w-full h-20 rounded-lg border-[1.4px] border-[#d1d1d1] px-3 py-2 text-[13px] font-poppins text-[#0d262e] placeholder:text-[#425b6e] outline-none resize-none"
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="pl-6 pr-8 pb-6 pt-4 flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="h-10 w-[140px] rounded-md border-[1.4px] border-[#003366] text-[#003366] text-xs font-poppins font-normal hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!isCompleteEnabled}
            onClick={() => {
              handleComplete();
            }}
            className={cn(
              'h-10 w-[140px] rounded-md text-xs font-poppins font-normal transition-colors',
              isCompleteEnabled
                ? 'bg-[#2ea650] text-white'
                : 'bg-[#ababab] text-white cursor-not-allowed'
            )}
          >
            {t('common.complete')}
          </button>
        </div>
      </div>
    </div>
  );
}
